import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Building2,
    CalendarDays,
    Check,
    Clock3,
    Download,
    Eye,
    FileText,
    Globe2,
    Hash,
    Paperclip,
    Pencil,
    User,
    type LucideIcon,
} from 'lucide-react';
import { useDeleteTender, useTender, useUpdateTender } from '@/lib/api/hooks/tender';
import { ActionDropdown, Button, DeleteModal, PublishModal, UnpublishModal, type DropdownAction } from '@/components/ui';
import { showToast } from '@/lib/toast';
import { sanitizeHtml } from '@/utils/helpers/html';
import handleDownloadFromUrl from '@/utils/helpers/handleDownloadFromUrl';

type OverviewItem = {
    label: string;
    value: string;
    subValue?: string;
    icon: LucideIcon;
    iconClassName: string;
    iconContainerClassName: string;
};

type TimelineItem = {
    title: string;
    value: string;
    status: 'complete' | 'pending';
};

const DetailCard = ({
    icon: Icon,
    title,
    children,
}: {
    icon: LucideIcon;
    title: string;
    children: React.ReactNode;
}) => (
    <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <header className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#E8F6EE] text-[#0F8A47]">
                    <Icon className="h-4 w-4" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            </div>
        </header>
        <div className="px-6 py-5">{children}</div>
    </section>
);

const InfoTile = ({ item }: { item: OverviewItem }) => {
    const Icon = item.icon;

    return (
        <article className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm min-h-[96px]">
            <div className="flex items-start gap-4">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded ${item.iconContainerClassName}`}>
                    <Icon className={`h-5 w-5 ${item.iconClassName}`} />
                </div>
                <div className="min-w-0">
                    <p className="text-sm text-gray-500">{item.label}</p>
                    <p className="mt-1 text-base font-semibold text-gray-900 truncate">{item.value}</p>
                    {item.subValue && <p className="text-sm text-gray-600 mt-0.5">{item.subValue}</p>}
                </div>
            </div>
        </article>
    );
};

const TimelineRow = ({ item, isLast }: { item: TimelineItem; isLast: boolean }) => {
    const complete = item.status === 'complete';

    return (
        <div className="relative flex gap-4 pb-7 last:pb-0">
            {!isLast && (
                <span
                    className={`absolute left-[13px] top-9 bottom-0 w-px ${complete ? 'bg-emerald-600' : 'bg-gray-300'}`}
                    aria-hidden="true"
                />
            )}
            <div
                className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 ${
                    complete ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-gray-400 bg-white text-gray-400'
                }`}
            >
                {complete ? <Check className="h-4 w-4" /> : <Clock3 className="h-4 w-4" />}
            </div>
            <div className="min-w-0 pt-0.5">
                <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                <p className="text-sm text-gray-500 mt-0.5">{item.value}</p>
            </div>
        </div>
    );
};

const TenderDetailPage: React.FC = () => {
    const { id: tenderId } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);

    const { data: tenderData, isLoading, error } = useTender(tenderId);
    const tender = tenderData?.data;

    const { mutate: deleteTender, isPending: isDeleting } = useDeleteTender();
    const { mutate: updateTender, isPending: isUpdating } = useUpdateTender();

    const formatDateLong = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return 'N/A';
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const time = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).toLowerCase();
        return `${day} ${month} ${date.getFullYear()}, ${time}`;
    };

    const formatBidType = (bidType: string) => {
        if (!bidType) return 'Tender';
        return bidType
            .split('_')
            .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ');
    };

    const getDocumentFilename = (url: string) => {
        if (!url) return 'Document.pdf';
        const parts = url.split('/');
        return parts[parts.length - 1] || 'Document.pdf';
    };

    const getReferenceNumber = () => {
        const titleSuffix = tender?.tenderTitle?.match(/(\d+)\s*$/)?.[1];
        const numericSuffix = titleSuffix || tender?.tenderId?.replace(/\D/g, '').slice(-3) || '001';
        const year = tender?.datePublished ? new Date(tender.datePublished).getFullYear() : new Date().getFullYear();
        return `AFSA/TDR/${year}/${numericSuffix.padStart(3, '0')}`;
    };

    const daysRemaining = useMemo(() => {
        if (!tender?.endDate) return null;
        const endDate = new Date(tender.endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        return Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    }, [tender?.endDate]);

    const overviewItems: OverviewItem[] = useMemo(() => [
        {
            label: 'Bid Type',
            value: formatBidType(tender?.bidType || ''),
            subValue: 'Procurement method',
            icon: FileText,
            iconClassName: 'text-[#0F8A47]',
            iconContainerClassName: 'bg-[#E8F6EE80]',
        },
        {
            label: 'Closing Date',
            value: formatDateLong(tender?.endDate),
            subValue: '11:59 PM (WAT)',
            icon: CalendarDays,
            iconClassName: 'text-[#0F8A47]',
            iconContainerClassName: 'bg-[#E8F6EE80]',
        },
        {
            label: 'Published By',
            value: tender?.publishedByName || tender?.createdByName || 'N/A',
            subValue: tender?.datePublished ? formatDateTime(tender.datePublished) : 'Not published',
            icon: User,
            iconClassName: 'text-[#0F8A47]',
            iconContainerClassName: 'bg-[#E8F6EE80]',
        },
        {
            label: 'Reference No.',
            value: getReferenceNumber(),
            icon: Hash,
            iconClassName: 'text-[#0F8A47]',
            iconContainerClassName: 'bg-[#E8F6EE80]',
        },
        {
            label: 'Visibility',
            value: tender?.status === 'PUBLISH' ? 'Public' : 'Private',
            icon: Globe2,
            iconClassName: 'text-[#0F8A47]',
            iconContainerClassName: 'bg-[#E8F6EE80]',
        },
        // {
        //     label: 'Currency',
        //     value: 'NGN',
        //     icon: Building2,
        //     iconClassName: 'text-[#0F8A47]',
        //     iconContainerClassName: 'bg-[#E8F6EE80]',
        // },
    ], [tender?.bidType, tender?.createdByName, tender?.datePublished, tender?.endDate, tender?.publishedByName, tender?.status]);

    const timelineItems: TimelineItem[] = useMemo(() => {
        const items: TimelineItem[] = [
            {
                title: 'Draft Created',
                value: tender?.datePublished ? formatDateTime(tender.datePublished) : 'Not available',
                status: tender?.datePublished ? 'complete' : 'pending',
            },
            {
                title: 'Published',
                value: tender?.datePublished ? formatDateTime(tender.datePublished) : 'Not published yet',
                status: tender?.status === 'PUBLISH' ? 'complete' : 'pending',
            },
            {
                title: 'Closing Date',
                value: `${formatDateLong(tender?.endDate)}${tender?.endDate ? ', 11:59 PM' : ''}`,
                status: daysRemaining !== null && daysRemaining >= 0 ? 'pending' : 'complete',
            },
        ];

        return items;
    }, [daysRemaining, tender?.datePublished, tender?.endDate, tender?.status]);

    const handleEdit = () => {
        if (!tenderId) return;
        navigate(`/admin/tenders/edit/${tenderId}`);
    };

    const handlePreview = () => {
        if (!tender?.tenderUrl) return;
        window.open(tender.tenderUrl, '_blank', 'noopener,noreferrer');
    };

    const handleDeleteClick = () => {
        setDeleteModalOpen(true);
    };

    const handleStatusUpdateClick = () => {
        if (!tender) return;
        setStatusModalOpen(true);
    };

    const confirmDelete = () => {
        if (!tenderId) return;
        deleteTender({ tenderId }, {
            onSuccess: (response) => {
                if (response.success) {
                    showToast.success('Tender deleted successfully!');
                    setDeleteModalOpen(false);
                    navigate('/admin/tenders');
                }
            },
            onError: (requestError: Error) => {
                showToast.error('Failed to delete: ' + (requestError.message || 'Unknown error'));
            }
        });
    };

    const confirmStatusUpdate = () => {
        if (!tender || !tenderId) return;
        const newStatus = tender.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

        updateTender(
            { tenderId, data: { ...tender, status: newStatus } },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success(
                            newStatus === 'PUBLISH'
                                ? 'Tender published successfully!'
                                : 'Tender unpublished successfully!'
                        );
                        setStatusModalOpen(false);
                    }
                },
                onError: (requestError: Error) => {
                    showToast.error('Failed to update status: ' + (requestError.message || 'Unknown error'));
                }
            }
        );
    };

    const getDropdownActions = (): DropdownAction[] => {
        const actions: DropdownAction[] = [
            {
                label: 'Edit Tender',
                onClick: (e) => {
                    e.stopPropagation();
                    handleEdit();
                }
            },
            {
                label: tender?.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
                onClick: (e) => {
                    e.stopPropagation();
                    handleStatusUpdateClick();
                },
                disabled: isUpdating
            }
        ];

        if (tender?.status === 'DRAFT') {
            actions.push({
                label: 'Delete',
                onClick: (e) => {
                    e.stopPropagation();
                    handleDeleteClick();
                },
                variant: 'danger' as const,
                disabled: isDeleting
            });
        }

        return actions;
    };

    if (!tenderId) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold">Tender ID missing</h2>
                <button onClick={() => navigate('/admin/tenders')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Tenders
                </button>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#681628] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading tender...</p>
                </div>
            </div>
        );
    }

    if (error || !tender) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold text-red-600">Error loading tender</h2>
                <p className="mt-2 text-gray-600">{error?.message || 'Tender not found'}</p>
                <button onClick={() => navigate('/admin/tenders')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Tenders
                </button>
            </div>
        );
    }

    const statusClassName = tender.status === 'PUBLISH'
        ? 'bg-[#E8F6EE] text-[#0F8A47] border-[#CFEED7]'
        : 'bg-gray-100 text-gray-700 border-gray-200';

    const documentFilename = getDocumentFilename(tender.tenderUrl || '');

    return (
        <>
            <div className="space-y-6">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex flex-col gap-2 ">
                        <div className="flex flex-wrap items-center gap-4">
                            <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-[2rem]">
                                {tender.tenderTitle}
                            </h1>
                            <span className={`inline-flex items-center rounded-md border px-2 py-1 text-sm font-semibold ${statusClassName}`}>
                                {tender.status === 'PUBLISH' ? '• Published' : '• Draft'}
                            </span>
                        </div>
                  
                    </div>

                    <div className="flex items-center gap-3 lg:justify-end">
                        <Button
                            type="button"
                            variant='secondary'
                            onClick={handleEdit}
                            className=" items-center gap-2 border border-gray-200 bg-white whitespace-nowrap text-sm font-semibold  shadow-sm hover:bg-gray-50"
                        >
                            <Pencil className="h-4 w-4" />
                            Edit Tender
                        </Button>
                      {tender.tenderUrl &&  <Button
                            type="button"
                            onClick={handlePreview}
                            variant='secondary'
                            disabled={!tender.tenderUrl}
                            className="inline-flex items-center gap-2  border border-gray-200 bg-white px-5 text-sm font-semibold  shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <Eye className="h-4 w-4" />
                            Preview
                        </Button>}
                        
                        <div className="rounded border border-gray-200 bg-white shadow-sm">
                            <ActionDropdown id={`tender-${tenderId}`} actions={getDropdownActions()} />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[8fr_4fr]">
                    <div className="space-y-6">
                        <DetailCard icon={FileText} title="Tender Overview">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                                {overviewItems.map((item) => (
                                    <InfoTile key={item.label} item={item} />
                                ))}
                            </div>
                        </DetailCard>

                        <DetailCard icon={FileText} title="Tender Description">
                           {tender.description ? <p className="whitespace-pre-line text-base text-gray-600" dangerouslySetInnerHTML={{ __html: sanitizeHtml(tender.description) }} />
                           : <span className="text-gray-400 italic">No description provided.</span>}
                        </DetailCard>

                        {tender.tenderUrl && (
                            <DetailCard icon={Paperclip} title="Attached Documents">
                                <div className="rounded-xl border border-gray-200 p-4">
                                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between flex-wrap xl:gap-6">
                                        <div className="flex min-w-0 items-center gap-4">
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#FDECEC] text-[#D33B2F]">
                                                <svg className="h-7 w-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 13h1.7l.6.9.6-.9h1.6l-1.4 2 1.4 2h-1.7l-.5-.9-.6.9H8.5l1.4-2-1.4-2zm5.5 0h3v1h-2v.5h1.5v1H15v.5h2v1h-3v-4z" />
                                                </svg>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-semibold text-gray-900">{documentFilename}</p>
                                                <p className="mt-1 text-sm text-gray-500">Uploaded on {tender.datePublished ? formatDateTime(tender.datePublished) : 'N/A'}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={handlePreview}
                                                disabled={!tender.tenderUrl}
                                                className="inline-flex h-11 items-center gap-2 rounded-lg border border-[#B86A00] px-4 text-sm font-semibold text-[#B86A00] hover:bg-[#FFF8EF] disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <Eye className="h-4 w-4" />
                                                Preview File
                                            </button>
                                            {
                                                tender.tenderUrl &&  <button
                                                onClick={() =>  handleDownloadFromUrl({url: tender.tenderUrl})}
                                                className="inline-flex h-11 items-center gap-2 rounded-lg bg-[#0F8A47] px-4 text-sm font-semibold text-white hover:bg-[#0C7A3D]"
                                            >
                                                <Download className="h-4 w-4" />
                                                Download
                                            </button>
                                            }
                                           
                                        </div>
                                    </div>

                         
                                </div>
                            </DetailCard>
                        )}
                    </div>

                    <aside className="space-y-4">
                        <DetailCard icon={Building2} title="Procurement Details">
                            <div className="space-y-4 text-sm">
                                <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                    <p className="text-gray-500">Procurement Method</p>
                                    <p className="mt-1 font-medium text-gray-900">{formatBidType(tender.bidType)}</p>
                                </div>
                                <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                    <p className="text-gray-500">Eligibility</p>
                                    <p className="mt-1 font-medium text-gray-900">Public</p>
                                </div>
                                <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                    <p className="text-gray-500">Delivery Location</p>
                                    <p className="mt-1 font-medium text-gray-900">N/A</p>
                                </div>
                                <div className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                    <p className="text-gray-500">Estimated Value</p>
                                    <p className="mt-1 font-medium text-gray-900">—</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Procurement Category</p>
                                    <p className="mt-1 font-medium text-gray-900">Tender</p>
                                </div>
                            </div>
                        </DetailCard>

                        <DetailCard icon={CalendarDays} title="Timeline">
                            <div className="pt-1">
                                {timelineItems.map((item, index) => (
                                    <TimelineRow key={item.title} item={item} isLast={index === timelineItems.length - 1} />
                                ))}
                            </div>
                        </DetailCard>
                    </aside>
                </div>
            </div>

            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Tender"
                subtitle="Are you sure you want to delete this tender?"
                warningText="This action cannot be undone."
                isLoading={isDeleting}
                itemType="Tender"
            />

            {tender.status === 'DRAFT' ? (
                <PublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    title="Publish Tender"
                    subtitle="Are you sure you want to publish this tender?"
                    itemType="Tender"
                    isLoading={isUpdating}
                />
            ) : (
                <UnpublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    itemType="Tender"
                    isLoading={isUpdating}
                />
            )}
        </>
    );
};

export default TenderDetailPage;
